import "./TablePlan.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import {
  collection,
  deleteField,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../../firebase";
import Input from "../../../components/Input";
import { ReactComponent as AngleLeft } from "../../../images/AngleLeft.svg";
import emailjs from "../../../utility/emailjsWithFallback";
import { Button, Modal } from "react-bootstrap";
import * as XLSX from "xlsx";
import Receipt from "../../../images/Receipt.png";
import TableNoteProductsModal from "./TableNoteProductsModal";
import ENV from "../../../env";
import EmptyState from "../../../components/emptyState/EmptyState";

function TablePlan({ org: selectedOrg }) {
  const { t } = useTranslation("tablePlan");
  const alert = useAlert();

  const fileInputRef = useRef(null);

  const [tableAmount, setTableAmount] = useState(0);
  const [loader, setLoader] = useState(false);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [customName, setCustomName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [customNumber, setCustomNumber] = useState("");
  const [numberConflictError, setNumberConflictError] = useState("");
  const [showImportWarningModal, setShowImportWarningModal] = useState(false);
  const [isTableNoteModalOpen, setIsTableNoteModalOpen] = useState();
  const [isTablePlanActive, setIsTablePlanActive] = useState(
    Boolean(selectedOrg?.isTablePlanActive),
  );

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateTableNumber = async (number, excludeTableId = null) => {
    if (!number || number < 0.1) {
      return t("invalid_table_number");
    }

    const existingTables = await getTablesByOrgId(selectedOrg.id);
    const conflict = existingTables.find(
      (table) => table.number === parseFloat(number) && table.id !== excludeTableId,
    );

    return conflict ? t("table_number_already_exists") : null;
  };

  const getTablesByOrgId = async (orgId) => {
    try {
      const tablePlansRef = collection(db, "Tables");
      const q = query(tablePlansRef, where("orgId", "==", orgId));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error fetching tables:", error);
      return [];
    }
  };

  const updateTablePlanAmount = async () => {
    if (selectedOrg?.id) {
      const tablePlan = await getTablesByOrgId(selectedOrg.id);
      setTableAmount(tablePlan?.length || 0);
      setTables(tablePlan || []);
    }
  };

  useEffect(() => {
    setIsTablePlanActive(Boolean(selectedOrg?.isTablePlanActive));
    updateTablePlanAmount();
  }, [selectedOrg]);

  const toggleTablePlanActive = async () => {
    if (!selectedOrg?.id) {
      alert.show(t("table_plan_status_save_error"), { type: "error" });
      return;
    }

    const nextValue = !isTablePlanActive;
    setIsTablePlanActive(nextValue);

    try {
      await updateDoc(doc(db, "Organizations", selectedOrg.id), {
        isTablePlanActive: nextValue,
      });
      alert.show(
        nextValue ? t("table_plan_enabled") : t("table_plan_disabled"),
        { type: "success" },
      );
    } catch (error) {
      console.error("Error updating table plan status:", error);
      setIsTablePlanActive(!nextValue);
      alert.show(t("table_plan_status_save_error"), { type: "error" });
    }
  };

  const updateTableAmount = async () => {
    if (!selectedOrg.id) {
      alert.show(t("table_amount_not_updated"), { type: "error" });
      return;
    }

    try {
      setLoader(true);
      const existingTables = await getTablesByOrgId(selectedOrg.id);
      const existingCount = existingTables.length;

      if (tableAmount > existingCount) {
        const existingNumbers = existingTables
          .map((t) => parseFloat(t.number))
          .sort((a, b) => a - b);
        const availableNumbers = [];

        let nextNumber = 1;
        for (let i = 0; i < existingNumbers.length; i++) {
          while (nextNumber < existingNumbers[i]) {
            availableNumbers.push(nextNumber);
            nextNumber++;
          }
          nextNumber = existingNumbers[i] + 1;
        }

        while (availableNumbers.length < tableAmount - existingCount) {
          availableNumbers.push(nextNumber);
          nextNumber++;
        }

        const batch = writeBatch(db);
        for (let i = 0; i < tableAmount - existingCount; i++) {
          const newTableRef = doc(collection(db, "Tables"));
          batch.set(newTableRef, {
            orgId: selectedOrg.id,
            number: availableNumbers[i],
            amountToBePaid: 0,
            customName: "",
          });
        }
        await batch.commit();
      }

      if (existingCount > tableAmount) {
        const sortedTables = existingTables.sort(
          (a, b) => parseFloat(b.number) - parseFloat(a.number),
        );
        const tablesToDelete = sortedTables.slice(0, existingCount - tableAmount);

        const batch = writeBatch(db);
        for (const table of tablesToDelete) {
          batch.delete(doc(db, "Tables", table.id));
        }
        await batch.commit();
      }

      alert.show(t("table_amount_updated"), { type: "success" });
      const updatedTables = await getTablesByOrgId(selectedOrg.id);
      setTables(updatedTables || []);
    } catch (err) {
      alert.show(t("table_amount_not_updated"), { type: "error" });
      console.error("Error updating document: ", err);
    } finally {
      setLoader(false);
    }
  };

  const saveTableConfiguration = async () => {
    if (!selectedTable?.id) {
      alert.show(t("no_table_selected"), { type: "error" });
      return;
    }

    const numberError = await validateTableNumber(customNumber, selectedTable.id);
    if (numberError) {
      setNumberConflictError(numberError);
      alert.show(numberError, { type: "error" });
      return;
    }

    try {
      setLoader(true);
      setNumberConflictError("");

      const tableRef = doc(db, "Tables", selectedTable.id);
      await updateDoc(tableRef, {
        customName: customName.trim(),
        billingEmail: billingEmail.trim(),
        number: parseFloat(customNumber),
      });

      alert.show(t("table_config_saved"), { type: "success" });

      const updatedTables = await getTablesByOrgId(selectedOrg.id);
      setTables(updatedTables || []);

      const updatedTable = updatedTables.find((t) => t.id === selectedTable.id);
      if (updatedTable) {
        setSelectedTable(updatedTable);
      }
    } catch (err) {
      alert.show(t("table_config_save_error"), { type: "error" });
      console.error("Error updating table configuration:", err);
    } finally {
      setLoader(false);
    }
  };

  const sendBillEmail = async (customerEmail, checkoutUrl) => {
    try {
      if (!customerEmail || customerEmail.trim() === "") {
        console.error("Customer email is empty or undefined");
        return false;
      }

      const params = {
        orgMail: selectedOrg?.email,
        customerEmail: customerEmail,
        invoiceUrl: checkoutUrl,
        organization: selectedOrg?.organization,
      };

      await emailjs.send(
        ENV.emailJsServiceId,
        "template_meaehee",
        params,
        "user_Io6hQDXLobAbA5jUwZICt",
      );

      return true;
    } catch (error) {
      console.error("Error sending email:", error);
      return false;
    }
  };

  const sendBill = async () => {
    if (!selectedTable?.id) {
      alert.show(t("no_table_selected"), {
        type: "error",
      });
      return;
    }

    if (!billingEmail || billingEmail.trim() === "") {
      alert.show(t("billing_email_required"), {
        type: "error",
      });
      return;
    }

    try {
      setLoader(true);

      const ordersRef = collection(db, "Orders");
      const q = query(
        ordersRef,
        where("fromTable", "==", selectedTable.id),
        where("orgId", "==", selectedOrg.id),
        where("toBePaid", "==", true),
      );
      const querySnapshot = await getDocs(q);

      const orders = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (orders.length === 0) {
        alert.show(t("no_orders_found"), {
          type: "error",
        });
        return;
      }

      const response = await fetch(`${ENV.firebaseApiUrl}/invoice/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orgId: selectedOrg.id,
          customerEmail: billingEmail.trim(),
          orders: orders,
          paid: false,
          paymentMethod: "bankTransfer",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create invoice");
      }

      const invoiceData = await response.json();

      if (!invoiceData.success) {
        console.error("Invoice creation failed:", invoiceData.error);
        throw new Error("Invoice creation failed");
      }

      const emailSent = await sendBillEmail(
        billingEmail.trim(),
        `https://ticket.comitty.be/invoice/${invoiceData.invoiceId}`,
      );

      if (!emailSent) {
        alert.show(t("invoice_created_email_failed"), {
          type: "warning",
        });
      }

      const tableRef = doc(db, "Tables", selectedTable.id);
      await updateDoc(tableRef, {
        amountToBePaid: 0,
      });

      const ordersBatch = writeBatch(db);
      orders.forEach((order) => {
        const orderRef = doc(db, "Orders", order.id);
        ordersBatch.update(orderRef, {
          toBePaid: false,
          paymentType: "bankTransfer",
          status: deleteField(),
        });
      });
      await ordersBatch.commit();

      setSelectedTable({
        ...selectedTable,
        amountToBePaid: 0,
      });

      const updatedTables = await getTablesByOrgId(selectedOrg.id);
      setTables(updatedTables || []);

      alert.show(t("bill_sent_successfully"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("error_sending_bill"), {
        type: "error",
      });
      console.error("Error sending bill:", err);
    } finally {
      setLoader(false);
    }
  };

  const hideImportWarningModal = () => {
    setShowImportWarningModal(false);
  };

  const isRowComplete = (row, index) => {
    if (!row.TableNumber) {
      alert.show(t("table_number_missing", { index: index + 1 }));
      return false;
    }
    return true;
  };

  const readFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet);

          const dataToSave = [];
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const isComplete = isRowComplete(row, i);
            if (isComplete) {
              dataToSave.push({
                customName: row.CustomName ?? "",
                number: row.TableNumber,
                billingEmail: row.BillingEmail ?? "",
                orgId: selectedOrg?.id,
              });
            }
          }
          if (dataToSave.length > 0) {
            if (tables.length > 0) {
              const deleteBatch = writeBatch(db);
              tables.forEach((table) => {
                deleteBatch.delete(doc(db, "Tables", table.id));
              });
              await deleteBatch.commit();
            }

            const updateBatch = writeBatch(db);
            dataToSave.forEach((data) => {
              const newTableRef = doc(collection(db, "Tables"));
              updateBatch.set(newTableRef, data);
            });
            await updateBatch.commit();
            await updateTablePlanAmount();
          }
          resolve();
        } catch (error) {
          alert.show(t("error_importing_file"), { type: "error" });
          console.error("Error importing file:", error);
          reject(error);
        }
      };

      reader.onerror = () => {
        console.error("Error reading file");
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const importCsv = async (inputEvent) => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      return;
    }
    await readFile(file);
    inputEvent.target.value = "";
  };

  const exportToCsv = useCallback(() => {
    if (tables.length === 0) {
      alert.show(t("no_tables_to_export"), { type: "error" });
      return;
    }

    const tablesToExport = tables.map((table) => ({
      TableNumber: table.number,
      CustomName: table.customName || "",
      BillingEmail: table.billingEmail || "",
    }));

    const tablesWs = XLSX.utils.json_to_sheet(tablesToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, tablesWs, "Tables");

    const filename = `table_plan_${selectedOrg?.organization || "export"}.xlsx`;
    XLSX.writeFile(wb, filename);

    alert.show(t("export_successful"), { type: "success" });
  }, [tables]);

  const closeTablePlan = async () => {
    setIsTableNoteModalOpen(false);
    await updateTablePlanAmount();
  };

  return (
    <div className="container-fluid table-plan">
      <div className="row mx-0 align-items-start">
        <div className="col-xl-8">
          <div className="card themeCard">
            <div className="card-header border-0 p-sm-20px pb-0 pb-sm-0">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="m-0 tableHeading">{selectedOrg?.organization}</h5>
                <div className="table-plan-status-control">
                  <span
                    className={`table-plan-status-label ${
                      isTablePlanActive ? "is-active" : "is-inactive"
                    }`}
                  >
                    {isTablePlanActive ? t("active") : t("inactive")}
                  </span>
                  <button
                    type="button"
                    className={`table-plan-switch ${isTablePlanActive ? "is-active" : ""}`}
                    onClick={toggleTablePlanActive}
                    aria-pressed={isTablePlanActive}
                    aria-label={t("toggle_table_plan")}
                    disabled={loader}
                  >
                    <span />
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body p-sm-20px">
              <div className="table-plan-content">
                <div className="table-plan-content-header">
                  <p className="table-plan-description">{t("tablePlanDescription")}</p>
                  <div className={"table-plan-content-header-excel-buttons"}>
                    <input
                      type="file"
                      accept=".xlsx, .xls, .csv"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={importCsv}
                    />
                    <Button className="import-export-btn" onClick={exportToCsv}>
                      {t("export_csv")}
                    </Button>
                    <Button
                      className="import-export-btn"
                      onClick={() => setShowImportWarningModal(true)}
                    >
                      {t("import_csv")}
                    </Button>
                  </div>
                </div>

                {tables.length > 0 ? (
                  <div className="tables-grid">
                    {tables
                      .sort((a, b) => parseFloat(a.number) - parseFloat(b.number))
                      .map((table) => (
                        <div
                          key={table.id}
                          className={`table-card ${selectedTable?.id === table.id ? "selected" : ""} ${table.amountToBePaid > 0 ? "has-amount" : ""}`}
                          onClick={() => {
                            setSelectedTable(table);
                            setCustomName(table.customName || "");
                            setBillingEmail(table.billingEmail || "");
                            setCustomNumber(table.number.toString());
                            setNumberConflictError("");
                          }}
                        >
                          <div className="table-content">
                            <div className="table-number">{table.number}</div>
                            <div className="table-amount">
                              {table.amountToBePaid > 0
                                ? `€${table.amountToBePaid.toFixed(2)}`
                                : ""}
                            </div>
                          </div>
                          {table.customName && (
                            <div className="table-name">
                              {table.customName.length > 25
                                ? `${table.customName.substring(0, 25)}...`
                                : table.customName}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <EmptyState
                    title={t("no_tables_configured")}
                    text={t("use_right_menu_to_add_tables")}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="manage-org">
            <h4>
              {t("configuration")}
              {selectedTable ? ` - Table ${selectedTable.number}` : null}
            </h4>

            {selectedTable ? (
              <div className="item">
                <h4 className="add-product-title">
                  <div
                    className="add-product-title-back-btn"
                    onClick={() => {
                      setSelectedTable(null);
                      setCustomName("");
                      setBillingEmail("");
                      setCustomNumber("");
                      setNumberConflictError("");
                    }}
                  >
                    <AngleLeft /> <div className="flex-grow-1">{t("back")}</div>
                  </div>
                  <button
                    className="add-product-title-table-note-btn"
                    onClick={() => setIsTableNoteModalOpen(true)}
                  >
                    <img src={Receipt} alt="Receipt" />
                  </button>
                </h4>
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="mb-20">
                    <Input
                      label={t("table_number")}
                      placeholder={t("enter_table_number")}
                      id="tableNumber"
                      type="number"
                      step="0.1"
                      value={customNumber}
                      onChange={(e) => setCustomNumber(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+") {
                          e.preventDefault();
                        }
                      }}
                      min="0.1"
                    />
                    {numberConflictError && (
                      <div className="text-danger small mt-1">{numberConflictError}</div>
                    )}
                  </div>
                  <div className="mb-20">
                    <Input
                      label={t("custom_name")}
                      placeholder={t("enter_custom_name")}
                      id="customName"
                      type="text"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                    />
                  </div>
                  <div className="mb-20">
                    <Input
                      label={t("billing_email")}
                      placeholder={t("enter_billing_email")}
                      id="billingEmail"
                      type="email"
                      value={billingEmail}
                      onChange={(e) => setBillingEmail(e.target.value)}
                    />
                  </div>
                  <button
                    onClick={saveTableConfiguration}
                    className="themeBtn m-0 w-100 pos-submit-btn"
                    disabled={
                      loader || (billingEmail.trim() !== "" && !isValidEmail(billingEmail.trim()))
                    }
                  >
                    {loader ? t("saving") : t("save")}
                  </button>
                  <button
                    onClick={sendBill}
                    className="themeBtn m-0 w-100 pos-submit-btn mt-3"
                    disabled={
                      !billingEmail ||
                      billingEmail.trim() === "" ||
                      !isValidEmail(billingEmail.trim()) ||
                      loader ||
                      !selectedTable?.amountToBePaid ||
                      selectedTable.amountToBePaid <= 0
                    }
                  >
                    {loader ? t("saving") : t("send_bill")}
                  </button>
                </form>
              </div>
            ) : (
              <div className="item">
                <h5 className="subtitle">{t("table_amount")}</h5>
                <div className="text">{t("table_amount_description")}</div>
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="mb-20">
                    <Input
                      label={t("amount")}
                      placeholder="12"
                      id="tableAmount"
                      type="number"
                      value={tableAmount}
                      onChange={(e) => setTableAmount(e.target.value)}
                    />
                  </div>
                  <button
                    onClick={updateTableAmount}
                    className="themeBtn m-0 w-100 pos-submit-btn"
                    disabled={loader}
                  >
                    {t("update")}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal show={showImportWarningModal} onHide={hideImportWarningModal} centered>
        <Modal.Header closeButton>
          <div className="d-flex flex-column">
            <Modal.Title>{t("are_you_sure")}</Modal.Title>
            <p>{t("importing_warning")}</p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="liquidityEditForm">
            <button
              className="themeBtn w-100 mt-4"
              onClick={() => {
                fileInputRef.current?.click();
                hideImportWarningModal();
              }}
            >
              {t("continue")}
            </button>
            <button className="themeBtn btn-dark w-100 mt-4" onClick={hideImportWarningModal}>
              {t("cancel")}
            </button>
          </div>
        </Modal.Body>
      </Modal>
      <TableNoteProductsModal
        isOpen={isTableNoteModalOpen}
        table={selectedTable}
        onClose={closeTablePlan}
      />
    </div>
  );
}

export default TablePlan;
